import React, { CSSProperties, useContext, useEffect, useState } from "react";
import IOwTableCols from "../../../models/IOwTableCols";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IOwTableConfig from "../../../models/IOwTableConfig";
import OwLabel from "../OwLabel/OwLabel";
import UIService from "../../../services/UIService";
import * as _ from "lodash-es";
import OwSortHeader from "../OwSortHeader/OwSortHeader";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import ChevronToggle from "../ChevronToggle/ChevronToggle";
import { FormattedNumber } from "react-intl";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwTitle from "../OwTitle/OwTitle";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import { v4 } from "uuid";
import { OwIconButton } from "../OwIconButton/OwIconButton";

type Props = {
    /**
     * The ID to add to the table
     */
    id?: string;
    /**
     * The class to apply to the <table> tag
     */
    tableClassName?: string;
    /**
     * A style object to set on the <table> tag
     */
    style?: CSSProperties;
    /**
     * The columns interface array composing the table columns and configuration
     */
    columns: IOwTableCols[];
    /**
     * A function to determine if the Row has a detail view, and show a Chevron
     */
    rowDetailViewFn?: (row: any) => boolean; // This function will allow you to determine if the view even has a detail view
    /**
     * Allow the detail view to Always be visible to the user
     */
    detailViewAlwaysVisible?: boolean; // This property allows a detail view to always be visible
    /**
     * The main property from the Parent Row to select the Detail View data from
     */
    detailViewDataSetProp?: string; // This is the property from the main dataSet to iterate on and view the data from
    /**
     * The sub property from the 'detailViewDataSetProp' to select the Detail View data from
     */
    detailViewDataSetSubProp?: string; // If there is another property within the detail view data set to use for the rows
    /**
     * Seperate Data Set not off of the Parent Row to use for the Detail View Data Set.
     */
    detailViewDataSet?: any[];
    /**
     * The columns interface array composing the detail view table columns and configuration
     */
    detailViewColumns?: IOwTableCols[];
    /**
     * A details view title to display above the table or component
     */
    detailViewTitle?: string;
    /**
     * The details view component to use in place of the table
     */
    detailViewComponent?: any;
    /**
     * If using the built-in detail view table, show an activity indicator if fetching data
     */
    detailViewDataFetch?: boolean;
    /**
     * If using the built-in detail view table, show an activity indicator if loading the initial data
     */
    detailViewInitialData?: boolean;
    /**
     * Specify how many columns the detail view will span. Default is Total Columns - 1 (assumes an action column).
     */
    detailViewColSpan?: number;
    /**
     * The data set used in conjunction with the columns
     */
    dataSet: any[];
    /**
     * Is data currently being fetched, to show an activity indicator
     */
    dataFetch: boolean;
    /**
     * Is the initial data set currently being fetched.
     */
    initialData: boolean;
    /**
     * A table configuration object allowing some different stylings, and usability options
     */
    tableConfig?: IOwTableConfig;
    /**
     * A function called when the chevron is toggled.
     */
    chevronToggleCallbackFn?: (rowIndex: number, detailViewOpen: boolean) => void;
    /**
     * If the table has a detail view or not.
     */
    isDetailViewTable?: boolean;
    /**
     * Used in conjuction with the built-in detail view, will send the parent row index
     */
    parentRowIndex?: number;
    /**
     * Specify a custom row name, useful for capturing the right row, with many tables on a view
     */
    rowIdName?: string;
    /**
     * The current sort column
     */
    currentSortCol?: string;
    /**
     * Maintain the height of the table between data refreshes / loadings
     */
    maintainTableHeight?: boolean; // maintain the height of the table between data refresh
    /**
     * Check all rows of data
     */
    checkAllFn?: (checkAll: boolean) => void;
    /**
     * If we need a mixed mode detail view scenario, some rows with the built in table or some with the component
     */
    detailViewRowOverride?: (row: any, parentRowIndex?: number) => boolean;
    /**
     * For use with the Select All to show the total rows selected.
     */
    totalRows?: number;
    /**
     * The classname to add on the wrapping table div.
     */
    wrappingClassName?: string;
    /**
     * Show initial detail views as visible
     */
    initialDetailViewVisible?: boolean;
    /**
     * The class to apply to the <thead> tag
     */
    theadClassName?: string;
    /**
     * The property to use as the key for the row
     */
    rowKeyProp?: string;
};

export const OwFnTable: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const [isCheckAllChecked, setIsCheckAllChecked] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [rowDetailViewOpen, setRowDetailViewOpen] = useState<{ [key: number]: boolean }>({});
    const [rowIndexVal, setRowIndexVal] = useState<number | undefined>();

    useEffect(() => {
        if (props.dataSet.length) {
            const _rowDetailViewOpen = rowDetailViewOpen;

            for (let i = 0; i < props.dataSet.length; ++i) {
                // console.log("isInitialDetailViewVisible", props.initialDetailViewVisible);
                _rowDetailViewOpen[i] = props.initialDetailViewVisible || false;
                i++;
            }

            setRowDetailViewOpen(_rowDetailViewOpen);
        }
    }, [props.dataSet]);

    useEffect(() => {
        if (props.checkAllFn) {
            props.checkAllFn(isCheckAllChecked);
        }
    }, [isCheckAllChecked]);

    useEffect(() => {
        for (const col of props.columns) {
            if (col.hasSelectAll && col.selectAllFn) {
                col.selectAllFn(selectAll);
            }
        }
    }, [selectAll]);

    useEffect(() => {
        if (props.chevronToggleCallbackFn && rowIndexVal) {
            const detailViewOpen = rowDetailViewOpen[rowIndexVal];

            props.chevronToggleCallbackFn(rowIndexVal, detailViewOpen);
        }
    }, [rowDetailViewOpen]);

    const checkAll = () => {
        setIsCheckAllChecked(!isCheckAllChecked);
    };

    const selectAllRows = () => {
        setSelectAll(!selectAll);
    };

    const getTableHeaderRow = () => {
        return props.columns.map((col: IOwTableCols, colIndex: number) => {
            if (col.notVisible) {
                return null;
            }

            if (!_.isNil(col.isChecked) && !col.isChecked()) {
                return null;
            }

            let className = col.className || "";

            if (props.tableConfig?.alignHeadersMiddle) {
                className += " align-middle";
            }

            if (!col.isActionsCol) {
                let title = col.skipTitleTranslate ? (
                    col.title
                ) : (
                    <OwLabel
                        supplierModel={supplierModel}
                        isSupplierVendorOverride={col.isVendorOverride}
                        noBottomMargin={true}
                        title={col.title}
                        rowHasSort={props.tableConfig?.tableHasSort}
                    />
                );

                let sortableElem: null | any = null;

                if (col.isSortable && (col.sortOrder || col.sortOrderFn) && col.sortFn && props.currentSortCol) {
                    if (col.sortOrder) {
                        sortableElem = (
                            <OwSortHeader
                                title={col.title}
                                currentSortCol={props.currentSortCol}
                                currentSortOrder={col.sortOrder}
                                sortFn={col.sortFn}
                                property={col.valueProp}
                                supplierModel={supplierModel}
                                isSupplierVendorOverride={col.isVendorOverride}
                            />
                        );
                    } else if (col.sortOrderFn) {
                        sortableElem = (
                            <OwSortHeader
                                title={col.title}
                                currentSortCol={props.currentSortCol}
                                currentSortOrder={col.sortOrderFn()}
                                sortFn={col.sortFn}
                                property={col.valueProp}
                                supplierModel={supplierModel}
                                isSupplierVendorOverride={col.isVendorOverride}
                            />
                        );
                    }
                }

                let headerActionIconClass = "";

                if (_.isString(col.headerActionIcon)) {
                    headerActionIconClass = col.headerActionIcon;
                }

                if (_.isFunction(col.headerActionIcon)) {
                    headerActionIconClass = col.headerActionIcon();
                }

                if (col.headerActionFn) {
                    headerActionIconClass = `${headerActionIconClass} pointer`;
                }

                if (col.titleRenderFn) {
                    title = col.titleRenderFn(col);
                }

                return (
                    <th key={colIndex} scope={"col"} className={className}>
                        {sortableElem ? sortableElem : title}
                        {col.headerActionIcon ? (
                            <i
                                className={headerActionIconClass}
                                onClick={(e: any) => {
                                    if (col.headerActionFn) {
                                        col.headerActionFn(col.title);
                                    }
                                }}></i>
                        ) : null}
                    </th>
                );
            } else {
                let innerData: any = null;

                if (col.hasCheckAll) {
                    innerData = (
                        <div>
                            <span className={"state-circle me-4"}></span>
                            <OwCheckbox
                                className={"pointer"}
                                checked={isCheckAllChecked || selectAll}
                                truthyVal={true}
                                falsyVal={false}
                                name={"checkAll"}
                                fn={checkAll}
                            />
                        </div>
                    );
                }

                if (col.hasSelectAll) {
                    const selectAllCount = ` ${props.totalRows || 0}`;

                    innerData = (
                        <div className={"d-flex"}>
                            {innerData}
                            <div className={!innerData ? "" : "ms-2"}>
                                <span className={"text-underline pointer"} onClick={selectAllRows}>
                                    {selectAll ? (
                                        <OwIconButton iconClass={"fas fa-minus"} iconColorClass={"text-primary"} fn={selectAllRows} />
                                    ) : (
                                        <OwIconButton
                                            iconClass={"fas fa-check-double"}
                                            iconColorClass={"text-primary"}
                                            fn={selectAllRows}
                                        />
                                    )}
                                </span>
                            </div>
                        </div>
                    );
                }

                if (col.titleRenderFn) {
                    innerData = col.titleRenderFn(col);
                }

                return (
                    <th key={colIndex} className={className} scope={"col"}>
                        {innerData}
                    </th>
                );
            }
        });
    };

    const toggleChevron = (rowIndex: number) => {
        const _rowDetailViewOpen = { ...rowDetailViewOpen };

        _rowDetailViewOpen[rowIndex] = !_rowDetailViewOpen[rowIndex];

        console.log("toggleChevron", _rowDetailViewOpen[rowIndex], "Index: ", rowIndex);

        setRowIndexVal(rowIndex);
        setRowDetailViewOpen(_rowDetailViewOpen);
    };

    const getDetailViewComponent = (parentRowId: string, row: any, parentRowIndex: number) => {
        if (_.isFunction(props.detailViewComponent)) {
            return props.detailViewComponent({ parentRowId, row, parentRowIndex });
        } else {
            return props.detailViewComponent;
        }
    };

    const compileRows = (): any => {
        return props.dataSet.map((row: any, rowIndex: number) => {
            const rowData = props.columns.map((col, colIndex: number) => {
                if (col.notVisible) {
                    return null;
                }

                if (!_.isNil(col.isChecked) && !col.isChecked()) {
                    return null;
                }

                const scope = colIndex === 1 ? "row" : "";

                if (col.isActionsCol) {
                    let data: any;

                    if (col.renderFn) {
                        data = col.renderFn(row, rowIndex, props.isDetailViewTable, props.parentRowIndex);
                    }

                    let showChevron = false;

                    if (col.hasChevron) {
                        if (col.showChevronFn) {
                            showChevron = col.showChevronFn(row);
                        } else {
                            showChevron = true;
                        }
                    }

                    return (
                        <td key={v4()} scope={scope} className={col.className}>
                            <div className={col.hasChevron ? "d-flex" : ""}>
                                {col.hasChevron ? (
                                    <ChevronToggle
                                        className={"me-2 " + (showChevron ? "" : "d-none d-print-none")}
                                        toggleFn={() => {
                                            toggleChevron(rowIndex);
                                        }}
                                        isVisible={rowDetailViewOpen[rowIndex]}
                                        showChevron={showChevron}
                                    />
                                ) : null}
                                {data}
                            </div>
                        </td>
                    );
                } else {
                    let data: any;

                    let baseObject = row;
                    let hasBaseObject = false;
                    let baseObjectFound = false;

                    if (col.baseObject) {
                        hasBaseObject = true;
                    }

                    if (col.baseObject && row[col.baseObject]) {
                        baseObject = row[col.baseObject];
                        baseObjectFound = true;
                    }

                    if (baseObject[col.valueProp]) {
                        data = baseObject[col.valueProp];
                    }

                    if (col.valueSubProp && !col.isArray) {
                        if (data && data[col.valueSubProp]) {
                            data = data[col.valueSubProp];
                        } else {
                            data = "";
                        }
                    } else {
                        if (col.arrayIndex && col.valueSubProp) {
                            if (data[col.arrayIndex] && data[col.arrayIndex][col.valueSubProp]) {
                                data = data[col.arrayIndex][col.valueSubProp];
                            }
                        }
                    }

                    if (hasBaseObject && !baseObjectFound) {
                        data = "";
                    }

                    if (col.renderFn) {
                        if (col.renderFnEmitsRow) {
                            data = col.renderFn(row, rowIndex, props.isDetailViewTable, props.parentRowIndex);
                        } else {
                            data = col.renderFn(data, rowIndex, props.isDetailViewTable, props.parentRowIndex);
                        }
                    }

                    if (col.isNumberFormat) {
                        data = <FormattedNumber value={data} />;
                    }

                    if (col.translateValue) {
                        data = <OwLabel noBottomMargin={true} title={data.toLowerCase()} />;
                    }

                    if (!col.isTextInput) {
                        return (
                            <td key={v4()} className={col.className} scope={scope}>
                                {data}
                            </td>
                        );
                    } else {
                        return (
                            <td key={v4()} className={col.className} scope={scope}>
                                <OwInputWrapper
                                    fn={(e: any) => {
                                        if (col.inputFn) {
                                            col.inputFn(row, e);
                                        }
                                    }}
                                    name={col.title}
                                    value={data}
                                />
                            </td>
                        );
                    }
                }
            });

            let buildDetailView = props.tableConfig?.hasDetailView;

            if (props.rowDetailViewFn) {
                buildDetailView = props.rowDetailViewFn(row);
            }

            const rowClassName = props.tableConfig?.tableStriped ? (rowIndex % 2 ? "row-even" : "row-odd") : "";

            const key = props.rowKeyProp ? row[props.rowKeyProp] : v4();

            if (buildDetailView) {
                let detailViewDataSet: any[] = [];

                if (props.detailViewDataSetProp) {
                    let rowDetailViewDataSet = row[props.detailViewDataSetProp];

                    if (props.detailViewDataSetSubProp && !_.isArray(rowDetailViewDataSet)) {
                        rowDetailViewDataSet = rowDetailViewDataSet[props.detailViewDataSetSubProp];
                        // rowDetailViewDataSet = rowDetailViewDataSet.map((dataItem: any) => {
                        //     // @ts-expect-error
                        //     return dataItem[props.detailViewDataSetSubProp];
                        // });
                    }

                    detailViewDataSet = rowDetailViewDataSet;
                }

                if (props.rowDetailViewFn && props.rowDetailViewFn(row)) {
                    detailViewDataSet = row;
                }

                let _rowDetailViewOpen = rowDetailViewOpen[rowIndex];

                if (props.detailViewAlwaysVisible) {
                    _rowDetailViewOpen = true;
                }

                let dummyTd: any = null;
                let detailViewColSpan = props.columns.length;

                if (!props.tableConfig?.detailViewFullWidth) {
                    dummyTd = <td></td>;
                    detailViewColSpan = detailViewColSpan - 1;

                    if (props.detailViewColSpan) {
                        let dummyInsideTd: any;
                        detailViewColSpan = props.detailViewColSpan;
                        const numDummyTd = props.columns.length - props.detailViewColSpan;

                        for (let i = 0; i < numDummyTd; ++i) {
                            dummyInsideTd = (
                                <React.Fragment>
                                    {dummyInsideTd} <td></td>
                                </React.Fragment>
                            );
                        }

                        dummyTd = dummyInsideTd;
                    }
                }

                // This allows us to mix both the built in table, AND the component for rows in the table.
                let showDetailViewComponent = false;

                if (props.detailViewRowOverride) {
                    showDetailViewComponent = props.detailViewRowOverride(row, props.parentRowIndex);
                } else {
                    if (props.detailViewAlwaysVisible) {
                        showDetailViewComponent = true;
                    } else {
                        if (_rowDetailViewOpen) {
                            showDetailViewComponent = true;
                        }
                    }
                }

                const rowName = props.rowIdName ? props.rowIdName : "row";

                const rowId = `${rowName}-${rowIndex}`;

                return (
                    <tbody key={key} id={rowId} className={rowClassName}>
                        <tr>{rowData}</tr>
                        {rowDetailViewOpen[rowIndex] ? (
                            <tr>
                                {dummyTd}
                                {/*{props.tableConfig?.detailViewFullWidth ? null : <td></td>}*/}
                                <td colSpan={detailViewColSpan}>
                                    <div>
                                        {props.detailViewTitle ? <OwTitle title={props.detailViewTitle} tag={"h3"} /> : null}
                                        {showDetailViewComponent && props.detailViewComponent ? (
                                            getDetailViewComponent(rowId, row, rowIndex)
                                        ) : (
                                            <OwFnTable
                                                style={{ backgroundColor: "transparent" }}
                                                columns={props.detailViewColumns || []}
                                                dataSet={detailViewDataSet || []}
                                                dataFetch={props.detailViewDataFetch || false}
                                                initialData={props.detailViewInitialData || false}
                                                isDetailViewTable={true}
                                                parentRowIndex={rowIndex}
                                            />
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                );
            } else {
                const rowName = props.rowIdName ? props.rowIdName : "row";

                let idVal = `${rowName}-${rowIndex}`;

                if (!_.isNil(props.parentRowIndex)) {
                    idVal = `${rowName}-${props.parentRowIndex}-${rowIndex}`;
                }

                return (
                    <tr key={key} id={idVal} className={rowClassName}>
                        {rowData}
                    </tr>
                );
            }
        });
    };

    const addStaticTableHeight = () => {
        // if (tableRef && props.maintainTableHeight) {
        //     let height = tableRef.offsetHeight;
        //     console.log("addStaticTableHeight", height);
        //
        //     // tableRef.style.height = `${height}px`;
        // }
    };

    const removeStaticTableHeight = () => {
        // if (tableRef && props.maintainTableHeight) {
        //     // let height = tableRef.offsetHeight;
        //     // console.log("removeStaticTableHeight");
        //
        //     tableRef.style.height = ``;
        // }
    };

    const headerRow = getTableHeaderRow();

    let tableRows = (
        <tbody>
            <tr>
                <td colSpan={props.columns.length}></td>
            </tr>
        </tbody>
    );

    if (props.initialData && props.dataFetch) {
        tableRows = (
            <tbody>
                <tr>
                    <td className={"text-center align-middle"} colSpan={props.columns.length}>
                        <OwLabel title={"retrieving_initial_data"} noBottomMargin={true} leftIconClass={"fas fa-circle-notch fa-spin"} />
                    </td>
                </tr>
            </tbody>
        );
    }

    if (!props.initialData && props.dataFetch) {
        addStaticTableHeight();

        tableRows = (
            <tbody>
                <tr>
                    <td className={"text-center align-middle"} colSpan={props.columns.length}>
                        <OwLabel title={"retrieving_data"} noBottomMargin={true} leftIconClass={"fas fa-circle-notch fa-spin"} />
                    </td>
                </tr>
            </tbody>
        );
    }

    if (!props.initialData && !props.dataFetch) {
        removeStaticTableHeight();

        if (props.dataSet.length) {
            const compiledRows = compileRows();
            // compile the rows from the data set.
            if (props.tableConfig?.hasDetailView) {
                tableRows = <React.Fragment>{compiledRows}</React.Fragment>;
            } else {
                tableRows = <tbody>{compiledRows}</tbody>;
            }
        } else {
            tableRows = (
                <tbody>
                    <tr>
                        <td className={"text-center"} colSpan={props.columns.length}>
                            <OwLabel title={"no_rows_to_show"} noBottomMargin={true} />
                        </td>
                    </tr>
                </tbody>
            );
        }
    }

    let className = " table ";

    if (!UIService.isMainViewLight()) {
        className += " table-dark ";
    }

    if (props.tableConfig?.tableHover) {
        className += " table-hover ";
    }

    if (props.tableConfig?.tableStriped) {
        className += " table-striped ";
    }

    if (props.tableConfig?.tableSmall) {
        className += " table-sm ";
    }

    if (props.tableClassName) {
        className += props.tableClassName;
    }

    return (
        <div className={"OwTable " + props.wrappingClassName} id={props.id}>
            <table className={className} style={props.style}>
                <thead className={props.theadClassName}>
                    <tr>{headerRow}</tr>
                </thead>
                {tableRows}
            </table>
        </div>
    );
};
